import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";

// GDPR: redact anything shaped like an email address before an event leaves
// the browser. Events are plain serializable data at beforeSend time.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const scrubEmails = <T,>(event: T): T => {
  try {
    return JSON.parse(
      JSON.stringify(event).replace(EMAIL_RE, "[redacted-email]"),
    ) as T;
  } catch {
    return event;
  }
};

Sentry.init({
  dsn: "https://06ebbe0cf7803104f2f16ead57d080fd@o4508182547464192.ingest.de.sentry.io/4508436649672784",
  integrations: [
    Sentry.browserTracingIntegration(),
    // Replay keeps its privacy defaults: maskAllText, maskAllInputs and
    // blockAllMedia all true — do not relax them (admin screens show member PII).
    Sentry.replayIntegration(),
  ],
  // GDPR: no IP/user auto-attachment; emails scrubbed from every event.
  sendDefaultPii: false,
  beforeSend: (event) => scrubEmails(event),
  beforeSendTransaction: (event) => scrubEmails(event),
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Only propagate trace headers to our own API hosts.
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api(-staging)?\.gymlive\.app/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

import App from "./App";

const container = document.getElementById("root");
// eslint-disable-next-line
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
